import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { storage } from '../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    quote: '',
    logoLight: null,
    logoDark: null,
    image: null,
  });
  const [uploading, setUploading] = useState({ logoLight: false, logoDark: false, image: false });
  const [uploadProgress, setUploadProgress] = useState({ logoLight: 0, logoDark: 0, image: 0 });

  // Fetch testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'testimonials'));
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTestimonials(data);
      } catch (err) {
        setError('Failed to fetch testimonials');
      }
      setLoading(false);
    };
    fetchTestimonials();
  }, [success]);

  // Upload file to Firebase Storage
  const uploadFile = (file, path, type) => {
    return new Promise((resolve, reject) => {
      setUploading(prev => ({ ...prev, [type]: true }));
      setUploadProgress(prev => ({ ...prev, [type]: 0 }));
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(prev => ({ ...prev, [type]: progress }));
        },
        (error) => {
          setUploading(prev => ({ ...prev, [type]: false }));
          reject(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(url => {
            setUploading(prev => ({ ...prev, [type]: false }));
            resolve({ url, name: file.name });
          });
        }
      );
    });
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!formData.name.trim() || !formData.title.trim() || !formData.quote.trim() || !formData.logoLight || !formData.logoDark || !formData.image) {
      setError('All fields and images are required.');
      return;
    }
    try {
      const testimonialData = {
        ...formData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (editingTestimonial) {
        await updateDoc(doc(db, 'testimonials', editingTestimonial.id), testimonialData);
        setSuccess('Testimonial updated successfully!');
      } else {
        await addDoc(collection(db, 'testimonials'), testimonialData);
        setSuccess('Testimonial added successfully!');
      }
      setIsAdding(false);
      setEditingTestimonial(null);
      setFormData({ name: '', title: '', quote: '', logoLight: null, logoDark: null, image: null });
    } catch (err) {
      setError('Failed to save testimonial.');
    }
  };

  // Handle edit
  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      title: testimonial.title,
      quote: testimonial.quote,
      logoLight: testimonial.logoLight,
      logoDark: testimonial.logoDark,
      image: testimonial.image,
    });
    setIsAdding(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await deleteDoc(doc(db, 'testimonials', id));
      setSuccess('Testimonial deleted successfully!');
    } catch (err) {
      setError('Failed to delete testimonial.');
    }
  };

  // Remove image
  const removeImage = (type) => {
    setFormData(prev => ({ ...prev, [type]: null }));
  };

  return (
    <div className="admin-testimonials">
      <h2>Manage Testimonials</h2>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {isAdding ? (
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Quote *</label>
            <textarea
              value={formData.quote}
              onChange={e => setFormData({ ...formData, quote: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Logo (Light Mode) *</label>
            {formData.logoLight ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={formData.logoLight.url} alt="Logo Light" style={{ maxWidth: 60, borderRadius: 6, border: '1px solid #eee' }} />
                <span>{formData.logoLight.name}</span>
                <button type="button" className="remove-btn" onClick={() => removeImage('logoLight')}>Remove</button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  try {
                    const img = await uploadFile(file, `testimonials/logoLight_${Date.now()}_${file.name}`, 'logoLight');
                    setFormData(prev => ({ ...prev, logoLight: img }));
                  } catch (err) {
                    setError('Failed to upload logo (light mode)');
                  }
                }}
                disabled={uploading.logoLight}
              />
            )}
            {uploading.logoLight && <div>Uploading... {Math.round(uploadProgress.logoLight)}%</div>}
          </div>
          <div className="form-group">
            <label>Logo (Dark Mode) *</label>
            {formData.logoDark ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={formData.logoDark.url} alt="Logo Dark" style={{ maxWidth: 60, borderRadius: 6, border: '1px solid #eee' }} />
                <span>{formData.logoDark.name}</span>
                <button type="button" className="remove-btn" onClick={() => removeImage('logoDark')}>Remove</button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  try {
                    const img = await uploadFile(file, `testimonials/logoDark_${Date.now()}_${file.name}`, 'logoDark');
                    setFormData(prev => ({ ...prev, logoDark: img }));
                  } catch (err) {
                    setError('Failed to upload logo (dark mode)');
                  }
                }}
                disabled={uploading.logoDark}
              />
            )}
            {uploading.logoDark && <div>Uploading... {Math.round(uploadProgress.logoDark)}%</div>}
          </div>
          <div className="form-group">
            <label>Testimonial Image *</label>
            {formData.image ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={formData.image.url} alt="Testimonial" style={{ maxWidth: 80, borderRadius: 6, border: '1px solid #eee' }} />
                <span>{formData.image.name}</span>
                <button type="button" className="remove-btn" onClick={() => removeImage('image')}>Remove</button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  try {
                    const img = await uploadFile(file, `testimonials/image_${Date.now()}_${file.name}`, 'image');
                    setFormData(prev => ({ ...prev, image: img }));
                  } catch (err) {
                    setError('Failed to upload testimonial image');
                  }
                }}
                disabled={uploading.image}
              />
            )}
            {uploading.image && <div>Uploading... {Math.round(uploadProgress.image)}%</div>}
          </div>
          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={loading || uploading.logoLight || uploading.logoDark || uploading.image}>
              {editingTestimonial ? 'Update Testimonial' : 'Add Testimonial'}
            </button>
            <button type="button" className="cancel-btn" onClick={() => {
              setIsAdding(false);
              setEditingTestimonial(null);
              setFormData({ name: '', title: '', quote: '', logoLight: null, logoDark: null, image: null });
            }}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <button className="add-btn" onClick={() => {
            setIsAdding(true);
            setEditingTestimonial(null);
            setFormData({ name: '', title: '', quote: '', logoLight: null, logoDark: null, image: null });
          }}>
            Add New Testimonial
          </button>
          {loading ? (
            <div className="loading">Loading testimonials...</div>
          ) : testimonials.length === 0 ? (
            <div className="no-projects">No testimonials found.</div>
          ) : (
            <div className="projects-grid">
              {testimonials.map(t => (
                <div key={t.id} className="project-card">
                  <img src={t.image && t.image.url} alt={t.name} className="project-image" />
                  <div className="project-info">
                    <h3>{t.name}</h3>
                    <p className="project-category">{t.title}</p>
                    <p className="project-description" dangerouslySetInnerHTML={{ __html: t.quote.replace(/\n/g, '<br />') }} />
                    <div style={{ display: 'flex', gap: 8, margin: '8px 0' }}>
                      <img src={t.logoLight && t.logoLight.url} alt="Logo Light" style={{ maxWidth: 40, background: '#fff', borderRadius: 4 }} />
                      <img src={t.logoDark && t.logoDark.url} alt="Logo Dark" style={{ maxWidth: 40, background: '#222', borderRadius: 4 }} />
                    </div>
                    <div className="project-actions">
                      <button onClick={() => handleEdit(t)} className="edit-btn">Edit</button>
                      <button onClick={() => handleDelete(t.id)} className="delete-btn">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminTestimonials; 